"use client"

import type React from "react"

import { useState } from "react"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { getFieldType, getResolvedData } from "../shared/schema-utils"
import { FieldRenderer } from "../shared/field-renderer"
import { DraggableField } from "./draggable-field"
import type { ReactNode } from "react"

export interface CardPreviewProps {
	item: any
	dataSchema: any
	data?: any
	parentData?: any
	parentPath?: string[]
	editable?: boolean
	draggable?: boolean
	onFieldChange?: (path: string[], value: any) => void
	renderItem?: (item: any, parentData?: any, parentScope?: string, parentPath?: string[]) => ReactNode
	tabIndex?: number
	componentPath?: number[]
	onReorderField?: (
		tabIndex: number,
		componentPath: number[],
		fromRow: number,
		fromCol: number,
		toRow: number,
		toCol: number,
	) => void
	onRemoveField?: (tabIndex: number, componentPath: number[], rowIndex: number, colIndex: number) => void
}

export function CardPreview({
	item,
	dataSchema,
	data,
	parentData,
	parentPath = [],
	editable = false,
	draggable = true,
	onFieldChange,
	renderItem,
	tabIndex,
	componentPath,
	onReorderField,
	onRemoveField,
}: CardPreviewProps) {
	const [draggedPosition, setDraggedPosition] = useState<{ row: number; col: number } | null>(null)
	const [dragOverPosition, setDragOverPosition] = useState<{ row: number; col: number } | null>(null)
	const isMobile = typeof window !== "undefined" && window.innerWidth <= 1024

	const resolvedData = getResolvedData(item.scope, data, parentData)

	const getCurrentPath = () => {
		const scopeParts = item.scope
			? item.scope.split("/").filter((p: string) => p && p !== "#" && p !== "properties")
			: []
		return [...parentPath, ...scopeParts]
	}

	const currentPath = getCurrentPath()

	const getValueFromData = (key: string) => {
		if (!resolvedData || resolvedData === null) return ""
		if (resolvedData && resolvedData[key] !== undefined) {
			const value = resolvedData[key]
			if (typeof value === "boolean") return value
			if (value === null) return ""
			if (typeof value === "object") return JSON.stringify(value)
			return String(value)
		}
		return ""
	}

	const handleChange = (key: string, value: any) => {
		if (onFieldChange) {
			onFieldChange([...currentPath, key], value)
		}
	}

	const handleFieldDragStart = (e: React.DragEvent, rowIndex: number, colIndex: number) => {
		setDraggedPosition({ row: rowIndex, col: colIndex })
		e.dataTransfer.effectAllowed = "move"
		e.dataTransfer.setData("text/plain", `${rowIndex},${colIndex}`)
	}

	const handleFieldDragOver = (e: React.DragEvent, rowIndex: number, colIndex: number) => {
		e.preventDefault()
		e.stopPropagation()
		e.dataTransfer.dropEffect = "move"
		setDragOverPosition({ row: rowIndex, col: colIndex })
	}

	const handleFieldDragLeave = () => {
		setDragOverPosition(null)
	}

	const handleFieldDrop = (e: React.DragEvent, toRow: number, toCol: number) => {
		e.preventDefault()
		e.stopPropagation()

		if (
			draggedPosition &&
			(draggedPosition.row !== toRow || draggedPosition.col !== toCol) &&
			tabIndex !== undefined &&
			componentPath &&
			onReorderField
		) {
			onReorderField(tabIndex, componentPath, draggedPosition.row, draggedPosition.col, toRow, toCol)
		}

		setDraggedPosition(null)
		setDragOverPosition(null)
	}

	const handleFieldDragEnd = () => {
		setDraggedPosition(null)
		setDragOverPosition(null)
	}

	const handleRemoveField = (rowIndex: number, colIndex: number) => {
		if (tabIndex !== undefined && componentPath && onRemoveField) {
			onRemoveField(tabIndex, componentPath, rowIndex, colIndex)
		}
	}
	const styles = isMobile ? {gridTemplateColumns: '1fr'} : {gridTemplateColumns: 'repeat(2, minmax(0, 1fr))'}

	return (
		<Card className="rounded-none md:rounded-lg border border-border md:border-white">
			<CardHeader>
				<CardTitle>{item.header || "Card"}</CardTitle>
			</CardHeader>
			<CardContent>
				{(!resolvedData || resolvedData === null) && !editable ? (
					<p className="text-sm text-muted-foreground italic">No data available</p>
				) : item.keys && item.keys.length > 0 ? (
					<div className="space-y-4">
						{item.keys.map((keyOrRow: string | string[], rowIndex: number) => {
							const isRow = Array.isArray(keyOrRow)
							const keys = isRow ? keyOrRow : [keyOrRow]

							return (
								<div
									key={rowIndex}
									className="grid gap-4 relative"
									style={styles}

								>
									{keys.map((key: string, colIndex: number) => {
										const isDragging = draggedPosition?.row === rowIndex && draggedPosition?.col === colIndex
										const isDropTarget = dragOverPosition?.row === rowIndex && dragOverPosition?.col === colIndex
										const fieldType = getFieldType(key, item.scope, dataSchema)
										const value = getValueFromData(key)

										return (
											<DraggableField
												key={`${rowIndex}-${colIndex}`}
												rowIndex={rowIndex}
												colIndex={colIndex}
												draggable={draggable && onReorderField !== undefined}
												isDragging={isDragging}
												isDropTarget={isDropTarget}
												onDragStart={handleFieldDragStart}
												onDragOver={handleFieldDragOver}
												onDragLeave={handleFieldDragLeave}
												onDrop={handleFieldDrop}
												onDragEnd={handleFieldDragEnd}
												onRemove={onRemoveField ? handleRemoveField : undefined}
											>
												<FieldRenderer
													fieldKey={key}
													fieldType={fieldType}
													value={value}
													editable={editable}
													onChange={(val) => handleChange(key, val)}
												/>
											</DraggableField>
										)
									})}
								</div>
							)
						})}
					</div>
				) : (
					<p className="text-sm text-muted-foreground">No fields configured</p>
				)}
				{item.children && item.children.length > 0 && renderItem && (
					<div className="mt-4 space-y-4">
						{item.children.map((child: any, idx: number) => (
							<div key={idx}>{renderItem(child, resolvedData, item.scope, currentPath)}</div>
						))}
					</div>
				)}
			</CardContent>
		</Card>
	)
}

